// @djsem/analysis-contract — Campaign Pack Orchestrator (Prompt 22E).
//
// Assembles platform-ready campaign asset packs from AI artwork + video outputs.
// Derives asset requirements from campaign type templates. Does NOT post to social platforms.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** The type of campaign a pack is being assembled for; drives its default required asset template. */
export type DJSEMCampaignPackType =
  | "single_release" | "ep_release" | "album_release" | "dj_mix_release"
  | "event_promo" | "set_announcement" | "show_pack_promo" | "custom";

/** A platform/output surface a campaign pack's assets are being prepared for (metadata only — no posting). */
export type DJSEMCampaignTargetPlatform =
  | "instagram" | "tiktok" | "youtube" | "soundcloud" | "spotify_canvas"
  | "press_kit" | "projection" | "led_wall" | "custom";

/** The kind of asset a campaign requirement calls for. */
export type DJSEMCampaignRequiredAssetType =
  | "release_cover" | "square_social_artwork" | "vertical_story_artwork"
  | "vertical_social_video" | "square_social_video" | "youtube_visualizer"
  | "spotify_canvas_loop" | "teaser_clip" | "thumbnail"
  | "press_image" | "projection_loop" | "led_wall_loop";

/** Fulfillment status of a single campaign asset requirement. */
export type DJSEMCampaignAssetStatus =
  | "missing" | "draft" | "generated" | "needs_review" | "approved" | "blocked";

/** The creative brief driving a campaign pack: objective, tone, target platforms, and required asset types. */
export interface DJSEMCampaignPackBrief {
  id: string;
  projectId: string;
  releaseId?: string; setId?: string; brandWorldId?: string;
  campaignType: DJSEMCampaignPackType;
  title: string;
  objective: string;
  targetPlatforms: DJSEMCampaignTargetPlatform[];
  tone: string[];
  requiredAssetTypes: DJSEMCampaignRequiredAssetType[];
  createdAt: string;
  warnings: string[];
}

/** A single asset requirement within a campaign pack (asset type, export preset, and fulfillment status). */
export interface DJSEMCampaignAssetRequirement {
  id: string;
  campaignPackId: string;
  assetType: DJSEMCampaignRequiredAssetType;
  exportPresetId: string;
  required: boolean;
  status: DJSEMCampaignAssetStatus;
  selectedAssetId?: string;
  selectedAssetVariantId?: string;
  generationJobIds: string[];
  warnings: string[];
}

/** A campaign asset pack being assembled from a brief's requirements, tracking overall readiness. */
export interface DJSEMCampaignPack {
  id: string;
  projectId: string;
  releaseId?: string; setId?: string;
  briefId: string;
  title: string;
  status: "draft" | "generating" | "needs_review" | "ready" | "exported" | "blocked";
  requirements: DJSEMCampaignAssetRequirement[];
  approvedAssetVariantIds: string[];
  rightsWarnings: string[];
  entitlementWarnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** The exportable manifest of a campaign pack's approved asset files. */
export interface DJSEMCampaignPackManifest {
  id: string;
  campaignPackId: string;
  projectId: string;
  createdAt: string;
  assetVariantIds: string[];
  files: Array<{
    assetVariantId: string;
    filename: string;
    platform: string;
    width?: number; height?: number;
    durationMs?: number; format?: string; hash?: string;
  }>;
  warnings: string[];
}

/** A plan of AI artwork/video generation jobs needed to fulfil a campaign pack's outstanding requirements. */
export interface DJSEMCampaignGenerationPlan {
  campaignPackId: string;
  artworkJobs: Array<{ assetType: DJSEMCampaignRequiredAssetType; outputType: string; }>;
  videoJobs: Array<{ assetType: DJSEMCampaignRequiredAssetType; outputType: string; }>;
  totalEstimatedCredits: number;
  warnings: string[];
  blockers: string[];
}

/** Result of exporting a campaign pack's approved assets. */
export interface DJSEMCampaignPackExportResult {
  campaignPackId: string;
  manifestId: string;
  exportedAssetCount: number;
  warnings: string[];
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link CampaignPackOrchestratorService.createBrief}. */
export interface CreateCampaignBriefInput extends Omit<DJSEMCampaignPackBrief, "id" | "createdAt" | "warnings"> {
  id?: string; createdAt?: string;
}
/** Input for {@link CampaignPackOrchestratorService.createCampaignPack}. */
export interface CreateCampaignPackInput { brief: DJSEMCampaignPackBrief; requirements: DJSEMCampaignAssetRequirement[]; createdAt?: string; }
/** Input for {@link CampaignPackOrchestratorService.deriveAssetRequirements}. */
export interface DeriveCampaignRequirementsInput { campaignPackId: string; campaignType: DJSEMCampaignPackType; }
/** Input for {@link CampaignPackOrchestratorService.createGenerationPlan}. */
export interface CreateCampaignGenerationPlanInput { pack: DJSEMCampaignPack; creditsRemaining?: number; }
/** Input for {@link CampaignPackOrchestratorService.attachGeneratedAsset}. */
export interface AttachCampaignGeneratedAssetInput { pack: DJSEMCampaignPack; requirementId: string; assetId: string; assetVariantId: string; reviewStatus: "approved" | "rejected" | "needs_review"; }
/** Input for {@link CampaignPackOrchestratorService.validateCampaignPack}. */
export interface ValidateCampaignPackInput { pack: DJSEMCampaignPack; }
/** Input for {@link CampaignPackOrchestratorService.createManifest}. */
export interface CreateCampaignManifestInput { pack: DJSEMCampaignPack; files: DJSEMCampaignPackManifest["files"]; createdAt?: string; }
/** Input for {@link CampaignPackOrchestratorService.exportCampaignPack}. */
export interface ExportCampaignPackInput { pack: DJSEMCampaignPack; manifest: DJSEMCampaignPackManifest; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// Default campaign templates
const ARTWORK_TYPES: DJSEMCampaignRequiredAssetType[] = ["release_cover", "square_social_artwork", "vertical_story_artwork", "thumbnail", "press_image"];
const VIDEO_TYPES: DJSEMCampaignRequiredAssetType[] = ["vertical_social_video", "square_social_video", "youtube_visualizer", "spotify_canvas_loop", "teaser_clip", "projection_loop", "led_wall_loop"];

const TEMPLATES: Record<DJSEMCampaignPackType, { required: DJSEMCampaignRequiredAssetType[]; optional: DJSEMCampaignRequiredAssetType[] }> = {
  single_release: { required: ["release_cover", "square_social_artwork", "vertical_story_artwork", "vertical_social_video", "spotify_canvas_loop", "thumbnail"], optional: ["press_image", "youtube_visualizer"] },
  ep_release: { required: ["release_cover", "square_social_artwork", "vertical_story_artwork", "vertical_social_video", "spotify_canvas_loop"], optional: ["youtube_visualizer", "thumbnail", "press_image"] },
  album_release: { required: ["release_cover", "square_social_artwork", "vertical_story_artwork", "vertical_social_video", "youtube_visualizer"], optional: ["spotify_canvas_loop", "thumbnail", "press_image"] },
  dj_mix_release: { required: ["release_cover", "vertical_social_video", "square_social_artwork", "thumbnail"], optional: ["youtube_visualizer", "spotify_canvas_loop"] },
  event_promo: { required: ["square_social_artwork", "vertical_story_artwork", "teaser_clip"], optional: ["vertical_social_video", "press_image"] },
  set_announcement: { required: ["square_social_artwork", "vertical_story_artwork"], optional: ["teaser_clip"] },
  show_pack_promo: { required: ["square_social_artwork", "vertical_social_video", "projection_loop"], optional: ["led_wall_loop", "vertical_story_artwork"] },
  custom: { required: [], optional: [] },
};

const PRESET_MAP: Partial<Record<DJSEMCampaignRequiredAssetType, string>> = {
  release_cover: "3000x3000_png", square_social_artwork: "1080x1080_png", vertical_story_artwork: "1080x1920_png",
  vertical_social_video: "1080x1920_mp4", square_social_video: "1080x1080_mp4",
  youtube_visualizer: "1920x1080_mp4", spotify_canvas_loop: "1080x1920_loop_mp4",
  teaser_clip: "1080x1920_mp4", thumbnail: "1920x1080_png",
  press_image: "3000x3000_png", projection_loop: "1920x1080_loop_mp4", led_wall_loop: "custom_loop_mp4",
};

const OUTPUT_TYPE_MAP: Record<DJSEMCampaignRequiredAssetType, string> = {
  release_cover: "release_cover", square_social_artwork: "social_square", vertical_story_artwork: "social_story",
  vertical_social_video: "vertical_social_clip", square_social_video: "social_teaser",
  youtube_visualizer: "youtube_visualizer_clip", spotify_canvas_loop: "spotify_canvas_loop",
  teaser_clip: "social_teaser", thumbnail: "thumbnail",
  press_image: "press_kit_image", projection_loop: "projection_loop", led_wall_loop: "led_wall_loop",
};

// ---- service ----------------------------------------------------------------

/** Assembles platform-ready campaign asset packs from AI artwork/video outputs; does not post to social platforms. */
export class CampaignPackOrchestratorService {
  /**
   * Create a new campaign pack brief, filling in id/createdAt defaults.
   * @param input - the brief's fields (excluding id/createdAt/warnings).
   * @returns the constructed brief.
   */
  createBrief(input: CreateCampaignBriefInput): DJSEMCampaignPackBrief {
    return {
      ...input,
      id: input.id ?? `cpb_${_seq.next()}`,
      createdAt: input.createdAt ?? AT,
      warnings: [],
    };
  }

  /**
   * Derive the required and optional asset requirements for a campaign type from its default template.
   * @param input - the campaign pack id and campaign type.
   * @returns the derived asset requirements, all initially "missing".
   */
  deriveAssetRequirements(input: DeriveCampaignRequirementsInput): DJSEMCampaignAssetRequirement[] {
    const tmpl = TEMPLATES[input.campaignType] ?? TEMPLATES.custom;
    const reqs: DJSEMCampaignAssetRequirement[] = [];
    for (const at of tmpl.required) {
      reqs.push({ id: `car_${_seq.next()}`, campaignPackId: input.campaignPackId, assetType: at, exportPresetId: PRESET_MAP[at] ?? "custom", required: true, status: "missing", generationJobIds: [], warnings: [] });
    }
    for (const at of tmpl.optional) {
      reqs.push({ id: `car_${_seq.next()}`, campaignPackId: input.campaignPackId, assetType: at, exportPresetId: PRESET_MAP[at] ?? "custom", required: false, status: "missing", generationJobIds: [], warnings: [] });
    }
    return reqs;
  }

  /**
   * Create a new draft campaign pack from a brief and its derived requirements.
   * @param input - the brief, its requirements, and optional creation time.
   * @returns the created campaign pack.
   */
  createCampaignPack(input: CreateCampaignPackInput): DJSEMCampaignPack {
    return {
      id: `cp_${_seq.next()}`,
      projectId: input.brief.projectId,
      releaseId: input.brief.releaseId, setId: input.brief.setId,
      briefId: input.brief.id,
      title: input.brief.title,
      status: "draft",
      requirements: input.requirements,
      approvedAssetVariantIds: [],
      rightsWarnings: [], entitlementWarnings: [],
      createdAt: input.createdAt ?? AT,
      updatedAt: input.createdAt ?? AT,
    };
  }

  /**
   * Build a plan of artwork/video generation jobs needed to fulfil a pack's outstanding (non-approved) requirements, with a credit estimate.
   * @param input - the pack and optionally the user's remaining credits (to flag insufficient-credit blockers).
   * @returns the generation plan (artwork/video jobs, estimated credits, warnings, blockers).
   */
  createGenerationPlan(input: CreateCampaignGenerationPlanInput): DJSEMCampaignGenerationPlan {
    const artworkJobs: DJSEMCampaignGenerationPlan["artworkJobs"] = [];
    const videoJobs: DJSEMCampaignGenerationPlan["videoJobs"] = [];
    const warnings: string[] = [];
    const blockers: string[] = [];

    for (const req of input.pack.requirements) {
      if (req.status === "approved") continue; // already fulfilled
      const outputType = OUTPUT_TYPE_MAP[req.assetType] ?? req.assetType;
      if (ARTWORK_TYPES.includes(req.assetType)) {
        artworkJobs.push({ assetType: req.assetType, outputType });
      } else if (VIDEO_TYPES.includes(req.assetType)) {
        videoJobs.push({ assetType: req.assetType, outputType });
      } else {
        warnings.push(`Unknown asset type: ${req.assetType}`);
      }
    }

    // Crude credit estimate: 2 per artwork, 5 per video
    const totalEstimatedCredits = artworkJobs.length * 2 + videoJobs.length * 5;
    if (input.creditsRemaining !== undefined && input.creditsRemaining < totalEstimatedCredits) {
      blockers.push(`Insufficient credits: have ${input.creditsRemaining}, need ~${totalEstimatedCredits}`);
    }

    return { campaignPackId: input.pack.id, artworkJobs, videoJobs, totalEstimatedCredits, warnings, blockers };
  }

  /**
   * Attach a generated asset variant to a specific requirement, updating its status per the review outcome.
   * @param input - the pack, target requirement id, generated asset/variant ids, and review status.
   * @returns the updated pack with the requirement (and approvedAssetVariantIds) updated.
   */
  attachGeneratedAsset(input: AttachCampaignGeneratedAssetInput): DJSEMCampaignPack {
    const reqs = input.pack.requirements.map(r => {
      if (r.id !== input.requirementId) return r;
      if (input.reviewStatus === "rejected") return { ...r, status: "blocked" as DJSEMCampaignAssetStatus, warnings: [...r.warnings, "Rejected asset cannot satisfy requirement."] };
      return {
        ...r,
        selectedAssetId: input.assetId,
        selectedAssetVariantId: input.assetVariantId,
        status: (input.reviewStatus === "approved" ? "approved" : "needs_review") as DJSEMCampaignAssetStatus,
      };
    });
    const approved = reqs.filter(r => r.status === "approved" && r.selectedAssetVariantId).map(r => r.selectedAssetVariantId!);
    return { ...input.pack, requirements: reqs, approvedAssetVariantIds: approved, updatedAt: AT };
  }

  /**
   * Validate that all required requirements are approved and none are blocked.
   * @param input - the pack to validate.
   * @returns validation result.
   */
  validateCampaignPack(input: ValidateCampaignPackInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    for (const r of input.pack.requirements) {
      if (r.required && r.status !== "approved") issues.push({ path: `$.requirements.${r.id}`, message: `required ${r.assetType} not approved (status: ${r.status})` });
      if (r.status === "blocked") issues.push({ path: `$.requirements.${r.id}`, message: `${r.assetType} blocked` });
    }
    if (input.pack.rightsWarnings.length > 0) issues.push({ path: "$.rightsWarnings", message: `${input.pack.rightsWarnings.length} rights warning(s)` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "campaign_pack");
  }

  /**
   * Build a manifest from candidate files, keeping only those tied to approved asset variants.
   * @param input - the pack, candidate files, and optional creation time.
   * @returns the constructed manifest (with a warning if any files were excluded as unapproved).
   */
  createManifest(input: CreateCampaignManifestInput): DJSEMCampaignPackManifest {
    const approvedFiles = input.files.filter(f => input.pack.approvedAssetVariantIds.includes(f.assetVariantId));
    const warnings: string[] = [];
    if (approvedFiles.length < input.files.length) warnings.push(`Excluded ${input.files.length - approvedFiles.length} unapproved asset(s) from manifest.`);
    return {
      id: `cpm_${_seq.next()}`,
      campaignPackId: input.pack.id,
      projectId: input.pack.projectId,
      createdAt: input.createdAt ?? AT,
      assetVariantIds: approvedFiles.map(f => f.assetVariantId),
      files: approvedFiles,
      warnings,
    };
  }

  /**
   * Export a campaign pack's manifest, counting only approved-asset files as exportable.
   * @param input - the pack and its manifest.
   * @returns the export result (exported count and any warnings).
   */
  exportCampaignPack(input: ExportCampaignPackInput): DJSEMCampaignPackExportResult {
    const warnings: string[] = [];
    // Only export approved
    const exportable = input.manifest.files.filter(f => input.pack.approvedAssetVariantIds.includes(f.assetVariantId));
    if (exportable.length === 0) warnings.push("No approved assets to export.");
    return {
      campaignPackId: input.pack.id,
      manifestId: input.manifest.id,
      exportedAssetCount: exportable.length,
      warnings,
    };
  }
}
